import moment from "moment";
import { DashboardAPI } from "utils/api";

export const getCliResults = async ({ cliId, modelId }) => {
    return DashboardAPI({
        method: "GET",
        url: "/getCliResults",
        query: {
            cliId,
            modelId,
        },
    });
};

export const getBatchModelSummary = async ({ modelIds }) => {
    return DashboardAPI({
        method: "POST",
        url: "/getBatchModelSummary",
        data: {
            modelIds,
        },
    });
};

export const sendH2kSimulation = async ({ modelId, userId, packages = [] }) => {
    return DashboardAPI({
        method: "POST",
        url: "/prepareSingleH2kSimulation",
        data: {
            houseModelId: modelId,
            userId,
            packages,
        },
    });
};

export const runH2kAzureSimulation = async ({ modelId, userId, packages = [] }) => {
    return DashboardAPI({
        method: "POST",
        url: "/runH2kAzureSimulation",
        data: `data=${JSON.stringify({
            modelId,
            userId,
            packages,
            callTime: moment().utc().format("YYYYMMDDHHmmssSS"),
        })}`,
        // data: {
        //     modelId,
        //     userId,
        //     packages,
        // },
        contentType: "application/x-www-form-urlencoded",
        preStringified: true,
    });
};

export const getDetailedHourlyResults = async ({ modelId, cliId, resultsCase, upgradePackageId = "" }) => {
    return DashboardAPI({
        method: "GET",
        url: "/getDetailedHourlyResults",
        query: {
            modelId,
            cliId,
            upgradePackageId,
            resultsCase,
        },
    });
};

export const getF280SummaryData = async ({ modelId, cliId, upgradePackageId = "" }) => {
    return DashboardAPI({
        method: "GET",
        url: "/getF280SummaryData",
        query: {
            modelId,
            cliId,
            upgradePackageId,
            returnType: "CSA_F280_D_REPORT",
        },
    });
};

export const calculateParametricCostForResults = async (analysisId) => {
    return DashboardAPI({
        method: "PUT",
        url: "/calculateParametricCostForResults",
        data: {
            analysisId,
        },
    });
};

/** Persists hourly series in separate Firestore docs; response includes IDs for the house model doc. */
export const uploadModelGreenButtonData = async ({ modelId, greenButtonXmlString, sourceFileName }) => {
    return DashboardAPI({
        method: "POST",
        url: "/uploadModelGreenButtonData",
        data: {
            modelId,
            greenButtonXmlString,
            sourceFileName,
        },
    });
};

/** Loads Green Button hourly/combined data for the model (same shape as upload response body). */
export const getModelGreenButtonData = async ({ modelId }) => {
    return DashboardAPI({
        method: "GET",
        url: "/getModelGreenButtonData",
        query: {
            modelId,
        },
    });
};

/** Normalizes axios success/error into a consistent Green Button payload shape. */
export const parseGreenButtonApiResponse = (axiosRes) => {
    if (!axiosRes) {
        return {
            ok: false,
            status: 0,
            body: null,
            hourly: [],
            documentIds: [],
            serverEmpty: true,
            error: "No response from server.",
        };
    }
    if (axiosRes.isAxiosError || (axiosRes.response?.status ?? 0) >= 400) {
        const status = axiosRes.response?.status ?? 0;
        const errBody = axiosRes.response?.data;
        const error =
            (typeof errBody?.message === "string" && errBody.message) ||
            axiosRes.message ||
            "Error loading green button data";
        return {
            ok: false,
            status,
            body: errBody ?? null,
            hourly: [],
            documentIds: [],
            serverEmpty: true,
            error,
        };
    }

    const status = axiosRes.status ?? 200;
    const raw = axiosRes.data ?? {};
    const unwrapped =
        raw?.hourlyGreenButtonData != null ||
        raw?.documentIds != null ||
        raw?.greenButtonDocumentIds != null ||
        raw?.empty != null
            ? raw
            : raw?.greenButtonData ?? raw?.data ?? raw;
    const body = unwrapped && typeof unwrapped === "object" ? unwrapped : {};
    const hourlyRaw = body?.hourlyGreenButtonData ?? [];
    const hourly = Array.isArray(hourlyRaw) ? hourlyRaw : [];
    const idsRaw = body?.documentIds ?? body?.greenButtonDocumentIds ?? [];
    const documentIds = Array.isArray(idsRaw) ? idsRaw : [];
    const serverEmpty =
        status === 204 || body?.empty === true || (documentIds.length === 0 && hourly.length === 0);

    return {
        ok: true,
        status,
        body,
        hourly,
        documentIds,
        serverEmpty,
        error: null,
    };
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Polls until hourly rows are available after an upload that returned document IDs only. */
export const waitForModelGreenButtonHourly = async ({ modelId, maxAttempts = 30, intervalMs = 2000 }) => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const res = await getModelGreenButtonData({ modelId });
        const parsed = parseGreenButtonApiResponse(res);
        if (!parsed.ok) {
            throw new Error(parsed.error);
        }
        if (parsed.hourly.length > 0) {
            return parsed.body;
        }
        if (parsed.serverEmpty && parsed.documentIds.length === 0) {
            // `empty: true` distinguishes a confirmed-empty collection from running out of attempts below.
            return { ...(parsed.body || {}), hourlyGreenButtonData: [], empty: true };
        }
        if (attempt < maxAttempts - 1) {
            await sleep(intervalMs);
        }
    }
    return null;
};

export const deleteModelGreenButtonData = async ({ modelId }) => {
    return DashboardAPI({
        method: "POST",
        url: "/deleteModelGreenButtonData",
        data: {
            modelId,
        },
    });
};

/** Legacy: merges weather + convolves Green Button hourly with bundled test heat-pump profile (`testHeatPumpData`). Prefer {@link getGreenButtonWeatherData}. */
export const analyzeGreenButtonWeatherData = async ({ modelId }) => {
    return DashboardAPI({
        method: "POST",
        url: "/analyzeGreenButtonWeatherData",
        data: {
            modelId,
            //testSliceLength: 200, //UNCOMMENT THIS LINE TO ONLY MERGE TEMP DATA WITH A SLICE OF UTILITY DATA (FASTER PROCESSING FOR TESTING)
        },
    });
};

/**
 * Fetches hourly weather for the model F280 location, merges temps into stored Green Button slices, clears combined
 * heat-pump bins in `greenButtonAnalysis`. Response `combinedUtilityHeatPumpData` is typically `[]`.
 *
 * Backend: `400` if F280 location is missing/invalid; `200` with `{ empty: true, message, ... }` when no GB docs;
 * `200` otherwise with `hourlyGreenButtonData` (same length as stored hours; missing weather → `temp: null`).
 * Older deployments may still return `204` for an empty GB collection.
 */
export const getGreenButtonWeatherData = async ({ modelId }) => {
    return DashboardAPI({
        method: "POST",
        url: "/getGreenButtonWeatherData",
        data: {
            modelId,
        },
    });
};

export const submitAnonCosts = async (analysisId) => {
    return DashboardAPI({
        method: "POST",
        url: "/submitAnonCosts",
        data: {
            analysisId,
        },
    });
};
